import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, MapPinIcon, StarIcon, UsersIcon, FilterIcon, MapIcon, ListIcon, DollarSignIcon, UtensilsIcon, CalendarIcon, ClockIcon, ChevronDownIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { RestaurantCard } from '../components/RestaurantCard';
import { PromotionBanner } from '../components/PromotionBanner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
import { restaurants } from '../data/restaurants';
// Remove mockRestaurants array and use imported restaurants instead
function getTodayDate() {
  const today = new Date();
  return today.toISOString().split('T')[0];
}
function getFutureTime() {
  const now = new Date();
  const futureTime = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours from now
  const hours = futureTime.getHours().toString().padStart(2, '0');
  const minutes = futureTime.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}
export function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedPackage, setSelectedPackage] = useState('all');
  const [selectedSeatingType, setSelectedSeatingType] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedTime, setSelectedTime] = useState(getFutureTime());
  const [partySize, setPartySize] = useState('2');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState<{
    restaurants: typeof mockRestaurants;
    cuisines: string[];
  }>({
    restaurants: [],
    cuisines: []
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showPartySizePicker, setShowPartySizePicker] = useState(false);
  const [showCuisinePicker, setShowCuisinePicker] = useState(false);
  const [showPricePicker, setShowPricePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showRatingPicker, setShowRatingPicker] = useState(false);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [showSeatingPicker, setShowSeatingPicker] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const partySizePickerRef = useRef<HTMLDivElement>(null);
  const cuisinePickerRef = useRef<HTMLDivElement>(null);
  const pricePickerRef = useRef<HTMLDivElement>(null);
  const locationPickerRef = useRef<HTMLDivElement>(null);
  const ratingPickerRef = useRef<HTMLDivElement>(null);
  const packagePickerRef = useRef<HTMLDivElement>(null);
  const seatingPickerRef = useRef<HTMLDivElement>(null);
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
        setShowTimePicker(false);
      }
      if (partySizePickerRef.current && !partySizePickerRef.current.contains(event.target as Node)) {
        setShowPartySizePicker(false);
      }
      if (cuisinePickerRef.current && !cuisinePickerRef.current.contains(event.target as Node)) {
        setShowCuisinePicker(false);
      }
      if (pricePickerRef.current && !pricePickerRef.current.contains(event.target as Node)) {
        setShowPricePicker(false);
      }
      if (locationPickerRef.current && !locationPickerRef.current.contains(event.target as Node)) {
        setShowLocationPicker(false);
      }
      if (ratingPickerRef.current && !ratingPickerRef.current.contains(event.target as Node)) {
        setShowRatingPicker(false);
      }
      if (packagePickerRef.current && !packagePickerRef.current.contains(event.target as Node)) {
        setShowPackagePicker(false);
      }
      if (seatingPickerRef.current && !seatingPickerRef.current.contains(event.target as Node)) {
        setShowSeatingPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Update autocomplete to use restaurants
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase();
      const matchingRestaurants = restaurants.filter(r => r.name.toLowerCase().includes(query) || r.description.toLowerCase().includes(query) || r.location.toLowerCase().includes(query)).slice(0, 5);
      const allCuisines = [...new Set(restaurants.map(r => r.cuisine))];
      const matchingCuisines = allCuisines.filter(c => c.toLowerCase().includes(query)).slice(0, 3);
      setAutocompleteResults({
        restaurants: matchingRestaurants,
        cuisines: matchingCuisines
      });
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
  }, [searchQuery]);
  const handleSelectRestaurant = (restaurant: (typeof mockRestaurants)[0]) => {
    setSearchQuery(restaurant.name);
    setShowAutocomplete(false);
  };
  const handleSelectCuisine = (cuisine: string) => {
    setSearchQuery(cuisine);
    setSelectedCuisine(cuisine);
    setShowAutocomplete(false);
  };
  const timeSlots = ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'];
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) || restaurant.description.toLowerCase().includes(searchQuery.toLowerCase()) || restaurant.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || restaurant.location === selectedLocation;
    const matchesCuisine = selectedCuisine === 'all' || restaurant.cuisine === selectedCuisine;
    const matchesPriceRange = selectedPriceRange === 'all' || restaurant.priceRange === selectedPriceRange;
    const matchesRating = restaurant.rating >= minRating;
    const matchesPackage = selectedPackage === 'all' || restaurant.specialPackages.includes(selectedPackage);
    const matchesSeating = selectedSeatingType === 'all' || restaurant.seatingType === selectedSeatingType;
    return matchesSearch && matchesLocation && matchesCuisine && matchesPriceRange && matchesRating && matchesPackage && matchesSeating;
  });
  const locations = ['all', ...new Set(restaurants.map(r => r.location))];
  const cuisines = ['all', ...new Set(restaurants.map(r => r.cuisine))];
  const priceRanges = ['all', '$', '$$', '$$$', '$$$$'];
  const packages = ['all', ...new Set(restaurants.flatMap(r => r.specialPackages))];
  const seatingTypes = ['all', 'indoor', 'outdoor'];
  return <div className="w-full min-h-screen" data-id="element-497">
      <Header data-id="element-498" />
      {/* Hero Section */}
      <div className="relative overflow-hidden" data-id="element-499">
        {/* Ramadan Background Image */}
        <div className="absolute inset-0" data-id="element-500">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iHLYcsuQ4NKZcERYfeBd1a/ramazan.png" alt="Ramadan Iftar" className="w-full h-full object-cover" data-id="element-501" />
          {/* Enhanced vignette effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900/60" data-id="element-502"></div>
        </div>
        {/* Decorative Pattern Overlay */}
        <div className="absolute inset-0 opacity-5" data-id="element-503">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-504">
            <defs data-id="element-505">
              <pattern id="wave-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-506">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="currentColor" fill="none" strokeWidth="2" data-id="element-507" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-pattern)" data-id="element-508" />
          </svg>
        </div>
        <div className="relative text-white py-28 px-4" data-id="element-509">
          <div className="max-w-6xl mx-auto" data-id="element-510">
            <div className="text-center mb-12" data-id="element-511">
              <h1 className="text-7xl font-bold mb-5 drop-shadow-2xl tracking-tight" data-id="element-512">
                Discover & Book Tables
              </h1>
              <p className="text-2xl opacity-95 font-light drop-shadow-xl tracking-wide" data-id="element-513">
                Your gateway to the finest dining experiences in Maldives
              </p>
            </div>
            {/* Enhanced Search Bar */}
            <div className="max-w-5xl mx-auto" data-id="element-514">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-slate-200" data-id="element-515">
                <div className="flex flex-col gap-4" data-id="element-516">
                  {/* Main Search Row with Autocomplete */}
                  <div className="flex flex-col md:flex-row gap-4" data-id="element-517">
                    <div className="flex-1 relative" ref={searchRef} data-id="element-518">
                      <div className="relative group" data-id="element-519">
                        <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 w-5 h-5 transition-colors z-10" data-id="element-520" />
                        <input type="text" placeholder="Search restaurants, cuisines, or locations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => searchQuery.length > 0 && setShowAutocomplete(true)} className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all hover:border-gray-300" data-id="element-521" />
                      </div>
                      {/* Autocomplete Dropdown */}
                      {showAutocomplete && (autocompleteResults.restaurants.length > 0 || autocompleteResults.cuisines.length > 0) && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-96 overflow-y-auto" data-id="element-522">
                            {/* Cuisines Section */}
                            {autocompleteResults.cuisines.length > 0 && <div className="p-3 border-b border-gray-100" data-id="element-523">
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2" data-id="element-524">
                                  Cuisines
                                </p>
                                {autocompleteResults.cuisines.map(cuisine => <button key={cuisine} onClick={() => handleSelectCuisine(cuisine)} className="w-full text-left px-4 py-2 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-3 group" data-id="element-525">
                                    <UtensilsIcon className="w-4 h-4 text-gray-400 group-hover:text-red-600" data-id="element-526" />
                                    <span className="text-gray-900 font-medium" data-id="element-527">
                                      {cuisine}
                                    </span>
                                  </button>)}
                              </div>}
                            {/* Restaurants Section */}
                            {autocompleteResults.restaurants.length > 0 && <div className="p-3" data-id="element-528">
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 px-2" data-id="element-529">
                                  Restaurants
                                </p>
                                {autocompleteResults.restaurants.map(restaurant => <button key={restaurant.id} onClick={() => handleSelectRestaurant(restaurant)} className="w-full text-left px-4 py-3 hover:bg-red-50 rounded-lg transition-colors group" data-id="element-530">
                                      <div className="flex items-center gap-3" data-id="element-531">
                                        <img src={restaurant.thumbnail} alt={restaurant.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-532" />
                                        <div className="flex-1" data-id="element-533">
                                          <p className="font-semibold text-gray-900 group-hover:text-red-600" data-id="element-534">
                                            {restaurant.name}
                                          </p>
                                          <p className="text-sm text-gray-500" data-id="element-535">
                                            {restaurant.cuisine} •{' '}
                                            {restaurant.location}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-1" data-id="element-536">
                                          <StarIcon className="w-4 h-4 text-amber-500 fill-current" data-id="element-537" />
                                          <span className="text-sm font-semibold text-gray-700" data-id="element-538">
                                            {restaurant.rating}
                                          </span>
                                        </div>
                                      </div>
                                    </button>)}
                              </div>}
                          </div>}
                    </div>
                  </div>
                  {/* Primary Filters - Beautified Custom Dropdowns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-539">
                    {/* Date Picker */}
                    <div className="relative" ref={datePickerRef} data-id="element-540">
                      <button type="button" onClick={() => setShowDatePicker(!showDatePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-541">
                        <CalendarIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-542" />
                        <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-543">
                          {formatDate(selectedDate)}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} data-id="element-544" />
                      </button>
                      {showDatePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 p-4" data-id="element-545">
                          <input type="date" value={selectedDate} onChange={e => {
                        setSelectedDate(e.target.value);
                        setShowDatePicker(false);
                      }} min={getTodayDate()} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" data-id="element-546" />
                        </div>}
                    </div>
                    {/* Time Picker */}
                    <div className="relative" ref={timePickerRef} data-id="element-547">
                      <button type="button" onClick={() => setShowTimePicker(!showTimePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-548">
                        <ClockIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-549" />
                        <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-550">
                          {formatTime(selectedTime)}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showTimePicker ? 'rotate-180' : ''}`} data-id="element-551" />
                      </button>
                      {showTimePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-64 overflow-y-auto" data-id="element-552">
                          {timeSlots.map(time => {
                        const timeValue = time.replace(' PM', '').replace(' AM', '').split(':');
                        let hour = parseInt(timeValue[0]);
                        const minute = timeValue[1];
                        if (time.includes('PM') && hour !== 12) hour += 12;
                        if (time.includes('AM') && hour === 12) hour = 0;
                        const value = `${hour.toString().padStart(2, '0')}:${minute}`;
                        return <button key={time} type="button" onClick={() => {
                          setSelectedTime(value);
                          setShowTimePicker(false);
                        }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedTime === value ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-553">
                                {time}
                              </button>;
                      })}
                        </div>}
                    </div>
                    {/* Party Size Picker */}
                    <div className="relative" ref={partySizePickerRef} data-id="element-554">
                      <button type="button" onClick={() => setShowPartySizePicker(!showPartySizePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-555">
                        <UsersIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-556" />
                        <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-557">
                          {partySize} {partySize === '1' ? 'Guest' : 'Guests'}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showPartySizePicker ? 'rotate-180' : ''}`} data-id="element-558" />
                      </button>
                      {showPartySizePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-64 overflow-y-auto" data-id="element-559">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <button key={n} type="button" onClick={() => {
                        setPartySize(n.toString());
                        setShowPartySizePicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${partySize === n.toString() ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-560">
                              {n} {n === 1 ? 'Guest' : 'Guests'}
                            </button>)}
                        </div>}
                    </div>
                    {/* Cuisine Picker */}
                    <div className="relative" ref={cuisinePickerRef} data-id="element-561">
                      <button type="button" onClick={() => setShowCuisinePicker(!showCuisinePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-562">
                        <UtensilsIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-563" />
                        <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-564">
                          {selectedCuisine === 'all' ? 'Any Cuisine' : selectedCuisine}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showCuisinePicker ? 'rotate-180' : ''}`} data-id="element-565" />
                      </button>
                      {showCuisinePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-64 overflow-y-auto" data-id="element-566">
                          {cuisines.map(cuisine => <button key={cuisine} type="button" onClick={() => {
                        setSelectedCuisine(cuisine);
                        setShowCuisinePicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedCuisine === cuisine ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-567">
                              {cuisine === 'all' ? 'Any Cuisine' : cuisine}
                            </button>)}
                        </div>}
                    </div>
                    {/* Price Range Picker */}
                    <div className="relative" ref={pricePickerRef} data-id="element-568">
                      <button type="button" onClick={() => setShowPricePicker(!showPricePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-569">
                        <DollarSignIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-570" />
                        <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-571">
                          {selectedPriceRange === 'all' ? 'Any Price' : selectedPriceRange}
                        </span>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showPricePicker ? 'rotate-180' : ''}`} data-id="element-572" />
                      </button>
                      {showPricePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50" data-id="element-573">
                          {priceRanges.map(range => <button key={range} type="button" onClick={() => {
                        setSelectedPriceRange(range);
                        setShowPricePicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedPriceRange === range ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-574">
                              {range === 'all' ? 'Any Price' : range}
                            </button>)}
                        </div>}
                    </div>
                  </div>
                  {/* Search Button */}
                  <button className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl" data-id="element-575">
                    Find Tables
                  </button>
                  {/* Advanced Filters Toggle */}
                  <div className="flex justify-center" data-id="element-576">
                    <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors" data-id="element-577">
                      <FilterIcon className="w-4 h-4" data-id="element-578" />
                      {showFilters ? 'Hide' : 'Show'} More Filters
                    </button>
                  </div>
                  {/* Expandable Advanced Filters */}
                  {showFilters && <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t-2 border-gray-200" data-id="element-579">
                      {/* Location Picker */}
                      <div className="relative" ref={locationPickerRef} data-id="element-580">
                        <button type="button" onClick={() => setShowLocationPicker(!showLocationPicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-581">
                          <MapPinIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-582" />
                          <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-583">
                            {selectedLocation === 'all' ? 'Any Location' : selectedLocation}
                          </span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showLocationPicker ? 'rotate-180' : ''}`} data-id="element-584" />
                        </button>
                        {showLocationPicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-64 overflow-y-auto" data-id="element-585">
                            {locations.map(loc => <button key={loc} type="button" onClick={() => {
                        setSelectedLocation(loc);
                        setShowLocationPicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedLocation === loc ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-586">
                                {loc === 'all' ? 'Any Location' : loc}
                              </button>)}
                          </div>}
                      </div>
                      {/* Rating Picker */}
                      <div className="relative" ref={ratingPickerRef} data-id="element-587">
                        <button type="button" onClick={() => setShowRatingPicker(!showRatingPicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-588">
                          <StarIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-589" />
                          <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-590">
                            {minRating === 0 ? 'Any Rating' : `${minRating}+ Stars`}
                          </span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showRatingPicker ? 'rotate-180' : ''}`} data-id="element-591" />
                        </button>
                        {showRatingPicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50" data-id="element-592">
                            {[0, 4, 4.5].map(rating => <button key={rating} type="button" onClick={() => {
                        setMinRating(rating);
                        setShowRatingPicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${minRating === rating ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-593">
                                {rating === 0 ? 'Any Rating' : `${rating}+ Stars`}
                              </button>)}
                          </div>}
                      </div>
                      {/* Package Picker */}
                      <div className="relative" ref={packagePickerRef} data-id="element-594">
                        <button type="button" onClick={() => setShowPackagePicker(!showPackagePicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-595">
                          <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-596">
                            {selectedPackage === 'all' ? 'Any Special Package' : selectedPackage}
                          </span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showPackagePicker ? 'rotate-180' : ''}`} data-id="element-597" />
                        </button>
                        {showPackagePicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 max-h-64 overflow-y-auto" data-id="element-598">
                            {packages.map(pkg => <button key={pkg} type="button" onClick={() => {
                        setSelectedPackage(pkg);
                        setShowPackagePicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedPackage === pkg ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-599">
                                {pkg === 'all' ? 'Any Special Package' : pkg}
                              </button>)}
                          </div>}
                      </div>
                      {/* Seating Type Picker - NEW */}
                      <div className="relative" ref={seatingPickerRef} data-id="element-600">
                        <button type="button" onClick={() => setShowSeatingPicker(!showSeatingPicker)} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white group" data-id="element-601">
                          <UtensilsIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-602" />
                          <span className="flex-1 text-left text-sm font-medium truncate" data-id="element-603">
                            {selectedSeatingType === 'all' ? 'Any Seating' : selectedSeatingType === 'indoor' ? 'Indoor' : 'Outdoor'}
                          </span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showSeatingPicker ? 'rotate-180' : ''}`} data-id="element-604" />
                        </button>
                        {showSeatingPicker && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50" data-id="element-605">
                            {seatingTypes.map(type => <button key={type} type="button" onClick={() => {
                        setSelectedSeatingType(type);
                        setShowSeatingPicker(false);
                      }} className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors ${selectedSeatingType === type ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-700'}`} data-id="element-606">
                                {type === 'all' ? 'Any Seating' : type === 'indoor' ? 'Indoor Seating' : 'Outdoor Seating'}
                              </button>)}
                          </div>}
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Promotions Banner */}
      <div className="max-w-6xl mx-auto px-4 -mt-12 relative z-10" data-id="element-607">
        <PromotionBanner data-id="element-608" />
      </div>
      {/* View Toggle & Results Count */}
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-609">
        <div className="flex items-center justify-between mb-8" data-id="element-610">
          <div data-id="element-611">
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-612">
              {filteredRestaurants.length} Restaurants Found
            </h2>
            <p className="text-gray-600 mt-1" data-id="element-613">
              {selectedPackage !== 'all' && `Showing ${selectedPackage} options`}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl shadow-md p-1" data-id="element-614">
            <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-615">
              <ListIcon className="w-5 h-5" data-id="element-616" />
              List
            </button>
            <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'map' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-617">
              <MapIcon className="w-5 h-5" data-id="element-618" />
              Map
            </button>
          </div>
        </div>
        {/* List View */}
        {viewMode === 'list' && <>
            {filteredRestaurants.length === 0 ? <div className="text-center py-16 bg-white rounded-2xl shadow-sm" data-id="element-619">
                <p className="text-gray-500 text-lg" data-id="element-620">
                  No restaurants found matching your criteria
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-621">
                {filteredRestaurants.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} data-id="element-622" />)}
              </div>}
          </>}
        {/* Map View */}
        {viewMode === 'map' && <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{
        height: '600px'
      }} data-id="element-623">
            <MapContainer center={[4.1755, 73.5093]} zoom={12} style={{
          height: '100%',
          width: '100%'
        }} data-id="element-624">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' data-id="element-625" />
              {filteredRestaurants.map(restaurant => <Marker key={restaurant.id} position={restaurant.coordinates} data-id="element-626">
                  <Popup data-id="element-627">
                    <div className="p-2" data-id="element-628">
                      <h3 className="font-bold text-lg mb-1" data-id="element-629">
                        {restaurant.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-630">
                        {restaurant.cuisine}
                      </p>
                      <div className="flex items-center gap-1 mb-2" data-id="element-631">
                        <StarIcon className="w-4 h-4 text-amber-500 fill-current" data-id="element-632" />
                        <span className="font-semibold" data-id="element-633">
                          {restaurant.rating}
                        </span>
                      </div>
                      <Link to={`/restaurant/${restaurant.id}`} className="text-red-600 hover:text-red-700 font-semibold text-sm" data-id="element-634">
                        View Details →
                      </Link>
                    </div>
                  </Popup>
                </Marker>)}
            </MapContainer>
          </div>}
      </div>
      {/* Decorative Footer Wave */}
      <div className="w-full" data-id="element-635">
        <svg className="w-full h-24 fill-current text-gray-900" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-636">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" data-id="element-637"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" data-id="element-638"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" data-id="element-639"></path>
        </svg>
      </div>
      <Footer data-id="element-640" />
    </div>;
}